import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import getAnswerLabel from '../../lib/getAnswerLabel';
import getVisibleFieldsFromForm from '../../lib/getVisibleFieldsFromForm';
import isInstantAnswerField from '../../lib/isInstantAnswerField';

import Blocks from '../blocks/Blocks';
import BasicButton from '../buttons/BasicButton';
import CloseButton from '../buttons/CloseButton';
import FormButton from '../buttons/FormButton';
import FormQuestion from '../forms/FormQuestion';
import CheckIcon from '../icons/CheckIcon';
import FormAnswers from '../partials/FormAnswers';
import Modal from './Modal';

import styles from '../../styles/popups/form-steps-modal.module.css';

type Answers = Record<string, unknown>;

interface FormStepsModalProps {
    form: Form;
    answers: Answers;
    completed?: boolean;
    initialField?: string | null;
    errors?: FormErrors | null;
    onAnswer: (fieldName: string, value: unknown) => Promise<Answers | null>;
    onComplete: (answers: Answers) => Promise<FormErrors | null>;
    onClosed?: (() => void) | null;
}

function isEmptyValue(value: unknown): boolean {
    return (
        value === null ||
        typeof value === 'undefined' ||
        value === '' ||
        (Array.isArray(value) && value.length === 0)
    );
}

/**
 * The questions of a form after the first one, asked one at a time, then the end of the form
 */
function FormStepsModal({
    form,
    answers,
    completed = false,
    initialField = null,
    errors = null,
    onAnswer,
    onComplete,
    onClosed = null,
}: FormStepsModalProps) {
    const { results = null } = form as Form & { results?: { blocks?: Block[] } | null };
    const { blocks: resultsBlocks = null } = results || {};
    const [opened, setOpened] = useState(true);
    const [loading, setLoading] = useState(false);
    const [values, setValues] = useState<Answers>(answers);
    const fields = getVisibleFieldsFromForm(form, answers);
    const [fieldName, setFieldName] = useState<string | null>(
        initialField || (fields.length > 0 ? fields[0].name : null),
    );
    const index = Math.max(
        fields.findIndex(({ name }) => name === fieldName),
        0,
    );
    const field = fields[index] || null;
    const [firstField = null] = fields;
    const firstAnswer =
        firstField !== null ? getAnswerLabel(firstField, answers[firstField.name]) : null;

    const requestClose = useCallback(() => setOpened(false), []);
    const onClickBackdrop = useCallback(() => {
        if (!loading) {
            setOpened(false);
        }
    }, [loading]);

    // Moves to the next question shown by the answers, or completes the form after the last one
    const gotoNext = useCallback(
        (newAnswers: Answers) => {
            const newFields = getVisibleFieldsFromForm(form, newAnswers);
            const nextIndex = newFields.findIndex(({ name }) => name === field.name) + 1;
            if (nextIndex > 0 && nextIndex < newFields.length) {
                setFieldName(newFields[nextIndex].name);
                return;
            }
            setLoading(true);
            onComplete(newAnswers).then((newErrors) => {
                setLoading(false);
                const errorField =
                    newErrors !== null
                        ? newFields.find(({ name }) => (newErrors[name] || []).length > 0) || null
                        : null;
                if (errorField !== null) {
                    setFieldName(errorField.name);
                }
            });
        },
        [form, field, onComplete],
    );

    const answer = useCallback(
        (value: unknown) => {
            // An optional question is skipped, an unchanged answer is already saved
            if (isEmptyValue(value) || value === answers[field.name]) {
                gotoNext(answers);
                return;
            }
            setLoading(true);
            onAnswer(field.name, value).then((newAnswers) => {
                setLoading(false);
                if (newAnswers !== null) {
                    gotoNext(newAnswers);
                }
            });
        },
        [answers, field, onAnswer, gotoNext],
    );

    const onChange = useCallback(
        (value: unknown) => {
            setValues((previous) => ({ ...previous, [field.name]: value }));
            if (isInstantAnswerField(field) && !isEmptyValue(value)) {
                answer(value);
            }
        },
        [field, answer],
    );

    const gotoPrevious = useCallback(() => {
        if (index > 0) {
            setFieldName(fields[index - 1].name);
        }
    }, [fields, index]);

    const gotoFirst = useCallback(() => {
        if (firstField !== null) {
            setFieldName(firstField.name);
        }
    }, [firstField]);

    // The keys 1 to 9 pick an option of the question
    useEffect(() => {
        const { options = null } = (field || {}) as OptionsField;
        if (completed || options === null || loading) {
            return () => {};
        }
        const onKeyDown = (event: KeyboardEvent) => {
            const { target, key } = event;
            if (target instanceof HTMLInputElement && target.type === 'text') {
                return;
            }
            const option = options[parseInt(key, 10) - 1] || null;
            if (option !== null) {
                onChange(option.value);
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [field, completed, loading, onChange]);

    return (
        <Modal
            id={`form-steps-${form.id}`}
            opened={opened}
            fullscreen
            withBackdrop
            withTransition
            withScrollLock
            requestClose={requestClose}
            onClosed={onClosed}
            className={styles.container}
            innerClassName={styles.inner}
        >
            {/* The dialog covers the screen on a phone, the backdrop around it closes it */}
            <div className={styles.backdrop} onClick={onClickBackdrop} aria-hidden="true" />
            <div
                className={styles.dialog}
                role="dialog"
                aria-modal="true"
                aria-labelledby={`form-steps-${form.id}-label`}
            >
                <div className={styles.header}>
                    <div className={styles.label} id={`form-steps-${form.id}-label`}>
                        <FormattedMessage
                            defaultMessage="Sondage"
                            description="Label of a survey inserted in an article"
                        />
                    </div>
                    <CloseButton onClick={requestClose} className={styles.close} />
                </div>
                <div className={styles.progress} aria-hidden="true">
                    {fields.map(({ name }, fieldIndex) => (
                        <span
                            key={`step-${name}`}
                            className={classNames([
                                styles.step,
                                {
                                    [styles.done]: completed || fieldIndex < index,
                                    [styles.active]: !completed && fieldIndex === index,
                                },
                            ])}
                        />
                    ))}
                </div>

                {completed ? (
                    <div className={styles.body}>
                        <CheckIcon
                            circleColor="var(--color-brand-default)"
                            className={styles.check}
                        />
                        {/* The results of the form already thank the reader */}
                        {resultsBlocks !== null ? (
                            <Blocks
                                blocks={resultsBlocks}
                                withoutMaxWidth
                                className={styles.results}
                            />
                        ) : (
                            <h2 className={styles.title}>
                                <FormattedMessage
                                    defaultMessage="Merci d’avoir répondu au sondage."
                                    description="Thank you message of a survey"
                                />
                            </h2>
                        )}
                        <FormAnswers fields={fields} answers={answers} />
                    </div>
                ) : null}

                {!completed && field !== null ? (
                    <div className={styles.body}>
                        {index > 0 && firstAnswer !== null ? (
                            <div className={styles.recap}>
                                <div className={styles.recapText}>
                                    <span className={styles.recapLabel}>
                                        <FormattedMessage
                                            defaultMessage="Ta réponse à la première question"
                                            description="Label of the first answer of a survey"
                                        />
                                    </span>
                                    <span className={styles.recapAnswer}>{firstAnswer}</span>
                                </div>
                                <BasicButton className={styles.edit} onClick={gotoFirst}>
                                    <FormattedMessage
                                        defaultMessage="Modifier"
                                        description="Button label"
                                    />
                                </BasicButton>
                            </div>
                        ) : null}
                        <FormQuestion
                            key={field.name}
                            field={field}
                            value={values[field.name] ?? null}
                            errors={errors !== null ? errors[field.name] || null : null}
                            loading={loading}
                            labelTag="h2"
                            labelClassName={styles.question}
                            descriptionClassName={styles.description}
                            fieldClassName={styles.field}
                            onChange={onChange}
                        />
                    </div>
                ) : null}

                <div className={styles.actions}>
                    {completed ? (
                        <FormButton className={styles.continue} onClick={requestClose}>
                            <FormattedMessage
                                defaultMessage="Retour à l’article"
                                description="Button label"
                            />
                        </FormButton>
                    ) : null}
                    {!completed && field !== null ? (
                        <>
                            {index > 0 ? (
                                <BasicButton className={styles.back} onClick={gotoPrevious}>
                                    <FormattedMessage
                                        defaultMessage="Retour"
                                        description="Button label"
                                    />
                                </BasicButton>
                            ) : null}
                            <FormButton
                                className={styles.continue}
                                loading={loading}
                                onClick={() => answer(values[field.name] ?? null)}
                            >
                                <FormattedMessage
                                    defaultMessage="Continuer"
                                    description="Button label"
                                />
                            </FormButton>
                        </>
                    ) : null}
                </div>
            </div>
        </Modal>
    );
}

export default FormStepsModal;
